import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Dropdown from '../../components/common/Dropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateSetupIntentMutation,
  useGetUserProfileQuery,
} from '../../redux/apiSlice';
import { toast } from 'react-toastify';
import { CircleChevronLeft, Loader2 } from 'lucide-react';
import LoginDetailsModal from '../common/LoginDetailsModal';
import { useSelector } from 'react-redux';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
);

const UpdatePaymentMethodForm: React.FC<{
  setIsUpdatePaymentModalOpen: (isOpen: boolean) => void;
}> = ({ setIsUpdatePaymentModalOpen }) => {
  const { data: userDetails, isLoading: userDetailsLoading } =
    useGetUserProfileQuery();
  const loggedInUser = useSelector((state: any) => state.app.user);
  console.log('userDetails', loggedInUser, userDetails);

  const stripe = useStripe();
  const elements = useElements();
  const [formData, setFormData] = useState({
    email: userDetails?.email || '',
    cardHolderName: '',
    country: '',
    postalCode: '',
  });
  const [createSetupIntent, { isLoading, error, isSuccess }] =
    useCreateSetupIntentMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange =
    (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      toast.error('Please enter your card details.');
      return;
    }

    try {
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement!,
          billing_details: {
            email: formData.email,
            name: formData.cardHolderName,
          },
        });

      if (paymentMethodError) {
        console.error('Payment Method Error:', paymentMethodError.message);
        toast.error(
          paymentMethodError.message || 'Failed to create payment method.',
        );
        return;
      }

      const { token, error } = await stripe.createToken(cardElement);
      console.log('token', token);

      const response: any = await createSetupIntent({
        user: loggedInUser?.user,
        paymentMethodId: token?.id,
        email: formData.email,
        name: formData.cardHolderName,
      }).unwrap();

      if (response?.paymentMethodId) {
        toast.success('Payment method updated successfully!');
        setIsUpdatePaymentModalOpen(false);
      } else {
        toast.error('Payment was not successful. Please try again.');
      }
    } catch (error) {
      console.error('Unexpected Error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
    }
  };

  const formStyles = 'mb-4';

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'UK', label: 'United Kingdom' },
  ];
  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={formStyles}>
          <label htmlFor="email" className="text-xs block font-medium">
            Email
          </label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="jane.diaz@example.com"
            disabled={true}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="cardHolderName" className="text-xs block font-medium">
            Name on card
          </label>
          <Input
            type="text"
            id="cardHolderName"
            name="cardHolderName"
            value={formData.cardHolderName}
            onChange={handleChange}
            placeholder="Jane Diaz"
            disabled={false}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="card" className="text-xs block font-medium">
            Card Information
          </label>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#333',
                  '::placeholder': {
                    color: '#aaa',
                  },
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a',
                },
              },
            }}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="country" className="text-xs block font-medium">
            Country or region
          </label>
          <Dropdown
            id="country"
            name="country"
            options={countryOptions}
            value={formData.country}
            onSelect={handleSelectChange('country')}
            placeholder="Select country"
            disabled={false}
          />
        </div>
        {/* <div className={formStyles}>
          <label htmlFor="postalCode" className="text-xs block font-medium">
            Postal Code
          </label>
          <Input
            type="text"
            id="postalCode"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="97712"
            disabled={false}
          />
        </div> */}
        <div className="flex gap-4">
          <Button
            type="button"
            className=" bg-gray-500"
            onClick={() => setIsUpdatePaymentModalOpen(false)}
          >
            Close
          </Button>
          <Button type="submit" className="" disabled={!stripe || isLoading}>
            {isLoading ? (
              <Loader2 className="animate-spin h-6 w-6 m-auto" />
            ) : (
              'Update'
            )}
          </Button>
        </div>
      </form>
    </>
  );
};

const UpdatePaymentMethod: React.FC<{
  setIsUpdatePaymentModalOpen: (isOpen: boolean) => void;
}> = ({ setIsUpdatePaymentModalOpen }) => {
  return (
    <Elements stripe={stripePromise}>
      <UpdatePaymentMethodForm
        setIsUpdatePaymentModalOpen={setIsUpdatePaymentModalOpen}
      />
    </Elements>
  );
};

export default UpdatePaymentMethod;
